import React, { useState, useRef, useEffect } from "react";
import { 
  Box, Typography, Grid, TextField, Paper, CircularProgress, 
  Button, IconButton, Stack, Dialog, DialogTitle, DialogContent, DialogActions 
} from "@mui/material";
import { 
  CloudUpload as CloudUploadIcon, 
  CameraAlt as CameraAltIcon, 
  Close as CloseIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function Declaration({ studentId, form, handleChange }) {
  const [uploading, setUploading] = useState(null);

  // Webcam States and Refs
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [activeSignatureType, setActiveSignatureType] = useState(null); // Tracks which signature is being captured
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // --- REUSABLE FIREBASE UPLOAD LOGIC ---
  const uploadImage = async (fileOrBlob, type) => {
    if (!fileOrBlob) return;

    try {
      setUploading(type);
      const storageRef = ref(
        storage,
        `signatures/${studentId}/${type}_${Date.now()}`
      );
      await uploadBytes(storageRef, fileOrBlob);
      const url = await getDownloadURL(storageRef);
      handleChange(type, url);
    } catch (err) {
      console.error(err);
      alert("Failed to upload signature. Please try again.");
    }
    setUploading(null);
  };

  // --- WEBCAM LOGIC ---
  const startCamera = async (type) => {
    setActiveSignatureType(type);
    setCameraOpen(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
    } catch (error) {
      console.error("Camera access denied:", error);
      alert("Camera access denied or unavailable.");
      setCameraOpen(false);
    }
  };

  // Attach stream to video element when it becomes available
  useEffect(() => {
    if (cameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [cameraOpen, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setCameraOpen(false);
    setStream(null);
    setActiveSignatureType(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && activeSignatureType) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to Blob and upload
      canvas.toBlob((blob) => {
        if (blob) {
          const typeToUpdate = activeSignatureType;
          stopCamera();
          uploadImage(blob, typeToUpdate);
        }
      }, "image/jpeg", 0.9);
    }
  };

  // Cleanup camera stream if component unmounts
  useEffect(() => {
    return () => stopCamera();
  }, []);

  const renderUpload = (type, label) => (
    <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
      <Box sx={{ border: "1px dashed #ccc", p: 2, borderRadius: 2, textAlign: "center", bgcolor: "#fafafa", height: "100%" }}>
        <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
          {label.toUpperCase()}
        </Typography>

        <Box sx={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center", mb: 2, border: "1px solid #eee", bgcolor: "#fff", borderRadius: 1 }}>
          {uploading === type ? (
            <CircularProgress size={30} />
          ) : form[type] ? (
            <img
              src={form[type]}
              alt={label}
              style={{ maxWidth: "100%", maxHeight: 120, display: "block", objectFit: "contain" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">No Signature</Typography>
          )}
        </Box>

        <Stack spacing={1} direction="column" alignItems="center">
          <Button
            variant="contained"
            component="label"
            size="small"
            fullWidth
            startIcon={<CloudUploadIcon />}
            disabled={uploading === type}
            sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" } }}
          >
            {uploading === type ? "Uploading..." : "Upload Image"}
            <input type="file" hidden accept="image/*" onChange={(e) => uploadImage(e.target.files[0], type)} />
          </Button>

          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<CameraAltIcon />}
            disabled={uploading === type}
            onClick={() => startCamera(type)}
            sx={{ color: "teal", borderColor: "teal", "&:hover": { borderColor: "#00695c" } }}
          >
            Capture Photo
          </Button>

          {form[type] && (
            <IconButton color="error" size="small" onClick={() => handleChange(type, "")}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Grid>
  );

  return (
    <Box display="flex" justifyContent="center" bgcolor="#f4f6f8" p={2}>
      <Paper sx={{ p: 3, width: "100%", maxWidth: "1200px" }}>

        <Box sx={{ background: "teal", color: "#fff", p: 1, mb: 3, borderRadius: 1 }}>
          <Typography fontWeight="bold">
            9) Declaration by Parents / Guardian
          </Typography>
        </Box>

        <Grid className="row" spacing={3}>
          <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Place"
              size="small"
              value={form.place || ""}
              onChange={(e) => handleChange("place", e.target.value)}
            />
          </Grid>

          <Grid className="col-sm-6 mb-3" item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.date || ""}
              onChange={(e) => handleChange("date", e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid className="row" spacing={2} mt={1}>
          {renderUpload("motherSignature", "Mother Signature")}
          {renderUpload("fatherSignature", "Father Signature")}
          {renderUpload("guardianSignature", "Guardian Signature")}
        </Grid>

      </Paper>

      {/* WEBCAM DIALOG */}
      <Dialog open={cameraOpen} onClose={stopCamera} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'teal', color: 'white' }}>
          <Typography variant="h6">Capture Signature</Typography>
          <IconButton aria-label="close" onClick={stopCamera} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#000' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", maxWidth: "480px", borderRadius: "8px", transform: "scaleX(-1)" }} // Mirrors the webcam
          />
          {/* Hidden Canvas for extracting the frame */}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<CameraAltIcon />} 
            onClick={capturePhoto}
            sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" }, px: 4, py: 1 }}
          >
            Snap & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Declaration;